import { AfterContentInit, AfterViewChecked, AfterViewInit, Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscriber, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { privileges } from 'src/app/constant';
import { Agency } from 'src/app/models/approvals/agency';
import { ApprovalTableRow } from 'src/app/models/approvals/approval-table-row';
import { ApprovalSet } from 'src/app/models/shared';
import { ColumnOrderBy } from 'src/app/models/shared/column-order-by';
import { TableColumn } from 'src/app/models/shared/table-column';
import { ApprovalsService, AuthService } from 'src/app/services';
import { LegacyReportsPaginationService } from 'src/app/services/legacy-reports-pagination.service';
import { PaginationService } from 'src/app/services/pagination.service';
import { ReportsPaginationService } from 'src/app/services/reports-pagination.service';


@Component({
  selector: 'legacy-reports-view',
  templateUrl: './legacy-reports-view.component.html',
  styleUrls: ['./legacy-reports-view.component.scss']
})
export class LegacyReportsViewComponent implements OnInit, OnDestroy {
  userIsCourt!: boolean;
  userIsSupervisor!: boolean;
  userIsAdmin!: boolean;

  @Input() courtInfoEditable!: boolean;
  @Input() tableColumns!: TableColumn[];
  @Input() columnOrderBy!: ColumnOrderBy; // overwrites default

  pageLoading!: boolean;

  filtersForm!: FormGroup;
  
  reportsRowId: string = "id";

  tablePage: number = 1; // modified by paginated table

  get tablePageData$() { return this.paginationService.tablePageData$; }
  get tableLoading() { return this.paginationService.tableLoading; }
  get ignoreTableLoading() { return this.paginationService.ignoreTableLoading; }

  get tablePageFrom() { return this.paginationService.tablePageFrom; }
  get tablePageTo() { return this.paginationService.tablePageTo; }

  getTableTotalLength() { return this.paginationService.tableTotalLength; }
  getTableFilteredLength() { return this.paginationService.tableFilteredLength; }

  columnOrderBySubscription!: Subscription | null;
  filterFormSubscription!: Subscription | null;

  get caseNumber() { return this.filtersForm?.get('caseNumber') as FormControl; }
  get agencyName() { return this.filtersForm?.get('agencyName') as FormControl; }
  get officerName() { return this.filtersForm?.get('officerLastName') as FormControl; }
  get officerBadgeNumber() { return this.filtersForm?.get('officerBadgeNumber') as FormControl; }
  get victimFirstName() { return this.filtersForm?.get('victimFirstName') as FormControl; }
  get victimLastName() { return this.filtersForm?.get('victimLastName') as FormControl; }
  get victimDriversLicenseState() { return this.filtersForm?.get('victimDriversLicenseState') as FormControl; }
  get victimDriversLicenseNumber() { return this.filtersForm?.get('victimDriversLicenseNumber') as FormControl; }
  get suspectFirstName() { return this.filtersForm?.get('suspectFirstName') as FormControl; }
  get suspectLastName() { return this.filtersForm?.get('suspectLastName') as FormControl; }
  get suspectSSN() { return this.filtersForm?.get('suspectSSN') as FormControl; }
  get suspectDriversLicenseState() { return this.filtersForm?.get('suspectDriversLicenseState') as FormControl; }
  get suspectDriversLicenseNumber() { return this.filtersForm?.get('suspectDriversLicenseNumber') as FormControl; }

  constructor(
    private reportsPaginationService: LegacyReportsPaginationService,
    private paginationService: PaginationService,
    private authService: AuthService,
    private approvalsService: ApprovalsService,
    private router: Router,
    private formBuilder: FormBuilder
  ) {
  }

  ngOnInit(): void {
    var defaultOrderBy = new ColumnOrderBy(this.tableColumns[0].name);
    this.tableColumns.forEach(x => {
      if (x.defaultOrderBy) {
        defaultOrderBy = new ColumnOrderBy(x.name);
      }
    });

    this.init();

    // No call to getReports necessary; this triggers a call automatically and is what populates the table on view load
    this.paginationService.setColumnOrderBy(defaultOrderBy);

    this.userIsSupervisor = this.authService.userHasPrivilege(privileges.supervisor);
    this.userIsCourt = this.authService.userHasPrivilege(privileges.court);
    this.userIsAdmin = this.authService.userHasPrivilege(privileges.administrator);

  }

  init() {
    this.tablePage = 1;
    this.paginationService.tablePage = 1;
    this.paginationService.tablePageLength = 10;

    this.filtersForm = this.formBuilder.group({
      approvalStatus: [null],
      caseNumber: [null],
      agencyName: [null],
      officerLastName: [null],
      officerBadgeNumber: [null],
      victimFirstName: [null],
      victimLastName: [null],
      victimDriversLicenseState: [null],
      victimDriversLicenseNumber: [null],
      suspectFirstName: [null],
      suspectLastName: [null],
      suspectSSN: [null],
      suspectDriversLicenseState: [null],
      suspectDriversLicenseNumber: [null]
    });

    this.filterFormSubscription = this.filtersForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(x => {
      this.tablePage = 1;
      this.paginationService.tablePage = 1;
      this.reportsPaginationService.getReports$();
    });

    this.columnOrderBySubscription = this.paginationService.columnOrderBy$.pipe(distinctUntilChanged()).subscribe(x => {
      this.reportsPaginationService.getReports$();
    });

    this.paginationService.setFiltersForm(this.filtersForm);
    this.approvalsService.setCourtInfoEditable(this.courtInfoEditable);
  }

  getReports() {
    this.paginationService.tablePage = this.tablePage;
    this.reportsPaginationService.getReports$();
  }

  getShowTotalReportsMessage(tableFilteredSize: number, totalSetsLength: number, tablePageFrom: number, tablePageTo: number) {
    if (tableFilteredSize == 0) return "No Reports Found.";
    return `Showing reports ${tablePageFrom + 1} to ${tablePageTo} of ${totalSetsLength} total`;
  }

  onRowClick(submissionId: string, courtInfoEditable: boolean) {
    var route = '/legacy/detail';
    if (courtInfoEditable) {
      route = '/court' + route;
    }
    this.router.navigate([route, submissionId]);
  }

  ngOnDestroy() {
    this.columnOrderBySubscription = null;
    this.filterFormSubscription = null;
  }

}
